import { useState, useEffect, useCallback } from 'react';

import {
  Paper,
  TableContainer,
  Table,
  TableHead as MUITableHead,
  TableRow,
  TableCell,
  TableBody as MUITableBody,
  InputLabel,
  Box,
} from '@mui/material';
import useAxios from 'axios-hooks';
import cloneDeep from 'lodash/cloneDeep';

import { Card } from 'components';
import strings, { url } from 'constants/index';
import { ColumnIds } from 'enums';
import { urlByCompetitorTypeMapping } from 'features/competitors/Competitors.utils';

interface Lineup {
  id: number;
  name: string;
  shirtNumber: string;
  position: string;
}

const columns = [
  {
    id: ColumnIds.Id,
    label: strings.id,
  },
  {
    id: ColumnIds.Name,
    label: strings.name,
  },
  {
    id: 'shirtNumber',
    label: strings.shirt_number,
  },
  {
    id: 'position',
    label: strings.position,
  },
];

enum ModeKeys {
  Contracts = 'contracts',
  Lineup = 'lineup',
}

const Lineup = ({ competitor }: { competitor: Competitor }) => {
  const [contracts, setContracts] = useState<Array<Lineup>>(null);
  const [lineup, setLineup] = useState<Array<Lineup>>([]);

  const { competitorType, id, name } = competitor;

  const [{ data }] = useAxios<PaginationData<any>>({
    url: `${urlByCompetitorTypeMapping[competitorType]}/${id}${url.contracts}`,
    params: { pageSize: 200 },
  });

  const handleDrag = useCallback(e => e.dataTransfer.setData(id, e.currentTarget.id), [id]);

  const handleDrop = useCallback(
    e => {
      e.stopPropagation();
      const lineupCoppied = cloneDeep(lineup);
      const contractsCoppied = cloneDeep(contracts);
      const dragItemIdData = e.dataTransfer.getData(id).split('-');
      const dragIndex = Number(dragItemIdData[1]);
      const dragMode = dragItemIdData[0];
      const dropItemIdData = e.currentTarget.id.split('-');
      const dropMode = dropItemIdData[0];
      const dropIndex = dropItemIdData[1] ? Number(dropItemIdData[1]) : dropItemIdData[1];

      if (dragMode === ModeKeys.Lineup) {
        if (dropMode === dragMode) {
          const dropItem = lineupCoppied[dropIndex];
          lineupCoppied[dropIndex] = lineupCoppied[dragIndex];
          lineupCoppied[dragIndex] = dropItem;

          setLineup(lineupCoppied);
        } else {
          const draggedItem = lineupCoppied[dragIndex];
          contractsCoppied.splice(dropIndex, 0, draggedItem);
          lineupCoppied.splice(dragIndex, 1);

          setContracts(contractsCoppied);
          setLineup(lineupCoppied);
        }
      } else if (dragMode === ModeKeys.Contracts && dropMode === ModeKeys.Lineup) {
        const draggedItem = contractsCoppied.splice(dragIndex, 1);
        if (dropIndex === undefined) {
          lineupCoppied.push(draggedItem[0]);
        } else {
          lineupCoppied.splice(dropIndex, 0, draggedItem[0]);
        }

        setContracts(contractsCoppied);
        setLineup(lineupCoppied);
      }
    },
    [contracts, lineup, id],
  );

  const handleDoubleClick = useCallback(
    e => {
      const contractsCoppied = cloneDeep(contracts);
      const lineupCoppied = cloneDeep(lineup);

      const dragItemIdData = e.currentTarget.id.split('-');
      const dragIndex = Number(dragItemIdData[1]);
      const dragMode = dragItemIdData[0];

      if (dragMode === ModeKeys.Contracts) {
        const draggedItem = contractsCoppied.splice(dragIndex, 1);
        lineupCoppied.push(draggedItem[0]);
      } else if (dragMode === ModeKeys.Lineup) {
        const draggedItem = lineupCoppied[dragIndex];
        contractsCoppied.push(draggedItem);
        lineupCoppied.splice(dragIndex, 1);
      }

      setContracts(contractsCoppied);
      setLineup(lineupCoppied);
    },
    [contracts, lineup],
  );

  useEffect(() => {
    if (data?.items && !contracts) {
      setContracts(
        data.items.map(item => ({
          id: item.id,
          name: item.competitorFrom?.name,
          shirtNumber: item.metadataProperties?.find(item => item.name === 'ShirtNumber')?.value,
          position: item.metadataProperties?.find(item => item.name === 'Position')?.value,
        })),
      );
    }
  }, [contracts, data]);

  const TableHead = () => (
    <MUITableHead>
      <TableRow>
        {columns.map(({ id, label }) => (
          <TableCell key={id} align="left">
            <InputLabel>{label}</InputLabel>
          </TableCell>
        ))}
      </TableRow>
    </MUITableHead>
  );

  const TableBody = ({ data, id }) => (
    <MUITableBody>
      {data.map((item, i: number) => (
        <TableRow
          key={item.id}
          id={`${id}-${i}`}
          draggable
          onDragOver={e => {
            if (e.dataTransfer.types[0] === id.toString()) {
              e.preventDefault();
            }
          }}
          onDragStart={e => handleDrag(e)}
          onDrop={handleDrop}
          onDoubleClick={handleDoubleClick}
          sx={{ cursor: 'pointer' }}
        >
          {columns.map(({ id }) => (
            <TableCell key={id} align="left">
              {item[id] || strings.not_applicable}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </MUITableBody>
  );

  return (
    <>
      <Box mb={2}>
        <Card title={`${name} - ${strings.lineup}`} contentClasses="nonPaddedContent">
          <TableContainer component={Paper}>
            <Table
              stickyHeader
              id="lineup"
              onDrop={handleDrop}
              onDragOver={e => {
                if (e.dataTransfer.types[0] === id.toString()) {
                  e.preventDefault();
                }
              }}
            >
              <TableHead />
              <TableBody data={lineup} id="lineup" />
            </Table>
          </TableContainer>
        </Card>
      </Box>
      <Card title={`${name} - ${strings.contracts}`} contentClasses="nonPaddedContent">
        <TableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead />
            <TableBody data={contracts || []} id="contracts" />
          </Table>
        </TableContainer>
      </Card>
    </>
  );
};

export default Lineup;
